import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { Confirm, Icons, Inform, Modal } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import Table from '@components/common/Table';
import { TApprovalUnitsFormSubmitHandler } from '@components/routes/AdminPanel/ManageApprovalUnits/ApprovalUnitsForm/ApprovalUnitsForm';

import { useApprovalUnits } from '@hooks/useManageApprovalUnit';

import { approveUnit } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setPage,
	setPageSize,
	setSearchValue,
	setSort,
	setUserId,
} from '@slices/approveUnit';

import { setFilterUtil } from '@utils/filterUtil';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { ICreateApprovalUnitDTO } from '@interfaces/organisation/dto/approvalUnitsDTO';
import { IApprovalUnit } from './interfaces';

import { EAdminPanelPageTitles } from '@enums/common';

import { MODAL_WIDTH_480 } from '@constants/modals';
import {
	addTooltipText,
	attachedInformText,
	informTitle,
	searchTooltipText,
} from './constants';

import ApprovalUnitsForm from './ApprovalUnitsForm';
import { getColumns } from './columns';

import styles from './ManageApprovalUnits.module.scss';

const ManageApprovalUnits = () => {
	const dispatch = useAppDispatch();

	const [isDeleteFormModal, setIsDeleteFormModal] = useState(false);
	const [isAttachedModal, setIsAttachedModal] = useState(false);
	const [isOpenUnitFormModal, setIsOpenUnitFormModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);

	const [editUnit, setEditUnit] = useState<IApprovalUnit | null>(null);
	const [deleteUnitId, setDeleteUnitId] = useState<string | null>(null);
	const [selectedUser, setSelectedUser] = useState<TSelectOption | null>(null);

	const toggleAttachedModal = () => setIsAttachedModal((prevState) => !prevState);
	const toggleDeleteModal = () => setIsDeleteFormModal((prevState) => !prevState);
	const toggleUnitFormModal = () => setIsOpenUnitFormModal((prevState) => !prevState);
	const toggleAddModal = useCallback(() => {
		setEditUnit(null);
		toggleUnitFormModal();
	}, []);

	const closeAttachedModal = () => setIsAttachedModal(false);
	const closeAddConfirm = () => setIsOpenUnitFormModal(false);
	const closeDeleteConfirm = () => {
		setIsDeleteFormModal(false);
		setDeleteUnitId(null);
	};

	const addUnitHandler = (data: ICreateApprovalUnitDTO) => {
		if (editUnit?.id) handleUpdateApprovalUnit({ data, unitId: editUnit.id });
		else handleCreateApprovalUnit(data);
	};

	const deleteUnitHandler = () => {
		if (deleteUnitId) handleDeleteApprovalUnit({ unitId: deleteUnitId });

		closeDeleteConfirm();
		setDeleteUnitId(null);
	};

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		userId,
	} = useAppSelector(approveUnit);

	const {
		approvalUnitData,
		countOfRecords,
		filterApprovalUsers,
		isLoadingApprovalUnit,
		handleCreateApprovalUnit,
		handleUpdateApprovalUnit,
		handleDeleteApprovalUnit,
	} = useApprovalUnits({
		page,
		pageSize,
		searchValue,
		sortDirection,
		sortFieldName,
		userId,
		onOpen: toggleAttachedModal,
		onClose: closeAddConfirm,
	});

	const handleSort = (sortBy: Array<ISortBy>) =>
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedUser,
			currentValue: userId,
			setAction: setUserId,
			fieldName: 'userId',
			dispatch,
		});
	}, [selectedUser, userId, dispatch]);

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const search: Array<ITableSearch> = [
		{
			initialValue: '',
			name: 'name',
			handler: (searchValue: string) => dispatch(setSearchValue({ searchValue })),
			tooltipText: searchTooltipText,
		},
	];

	const filters: Array<TTableFilter> = useMemo(() => [
		{
			name: 'name',
			placeholder: 'User name',
			option: selectedUser,
			options: filterApprovalUsers,
			minWidth: '200px',
			setState: setSelectedUser,
			defaultValue: userId,
		},
	], [filterApprovalUsers, selectedUser, userId]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: countOfRecords || 0,
		forcePage: page,
		size: pageSize,
		pageHandler: (page: number) => dispatch(setPage({ page })),
		pageSizeHandler: (pageSize: number) => dispatch(setPageSize({ pageSize })),
	}), [countOfRecords, pageSize, page, dispatch]);

	const columns = useMemo(() => {
		return getColumns({
			toggleDeleteModal,
			toggleEditModal: toggleUnitFormModal,
			setEditUnit,
			setDeleteUnitId,
		});
	}, []);

	return (
		<div className={styles.manageApprovalUnitsContainer}>
			<PageTitle
				icon={Icons.assetsRequestsLight()}
				title={EAdminPanelPageTitles.ManageApprovalUnits}
			/>
			<Modal
				isOpen={isAttachedModal}
				onClose={closeAttachedModal}
				title={informTitle}
				width={MODAL_WIDTH_480}
			>
				<Inform
					informText={attachedInformText}
					onClose={closeAttachedModal}
				/>
			</Modal>

			<Modal
				isOpen={isDeleteFormModal}
				onClose={closeDeleteConfirm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeDeleteConfirm}
					onSubmit={deleteUnitHandler}
					question='Are you sure you want to delete the Approval Unit?'
				/>
			</Modal>

			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isOpenUnitFormModal}
				onClose={closeAddConfirm}
				title={editUnit ? 'Edit approval unit' : 'Add approval unit'}
				width='440px'
			>
				<ApprovalUnitsForm
					editUnit={editUnit}
					onClose={closeAddConfirm}
					setIsDirtyFormModal={setIsDirtyFormModal}
					submitHandler={addUnitHandler as TApprovalUnitsFormSubmitHandler}
				/>
			</Modal>
			<div
				className={styles.unitsApprovalListTableContainer}
				data-testid='manage-approval-units-table'
			>
				<Table
					addTooltipText={addTooltipText}
					columns={columns}
					data={approvalUnitData}
					filters={filters}
					isCreatable
					isLoading={isLoadingApprovalUnit}
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					searchThreshold={0}
					sort={sort}
					toggleAddModal={toggleAddModal}
				/>
			</div>
		</div>
	);
};

export default memo(ManageApprovalUnits);
